import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { initiatePayment, paymentCallback, type PaymentCallbackResponse } from '../lib/api';
import { useKiosk } from '../lib/session';

/** 模擬 PSP 交易序號（demo；正式接金流由刷卡機 / PSP 回傳）。 */
function mockProviderTxId(): string {
  return `kiosk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 付款進行中 — Flow 3.11（不可中斷；IdleReset 暫停）。
 *
 * initiate_payment（HOLD→PAYMENT_PENDING）→ payment-callback（→PAID 同 tx 發票）。
 * paid → 出票完成頁；oversold / failed → 留頁顯示買家友善結果面板。
 */
export function PaymentProgressScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, draft, patchDraft, setIdleSuspended, resetSession } = useKiosk();
  const order = draft.order;
  const startedRef = useRef(false);

  const payMutation = useMutation({
    mutationFn: async (): Promise<PaymentCallbackResponse> => {
      if (order === null) throw new Error('missing order');
      await initiatePayment(order.id);
      return paymentCallback(order.id, mockProviderTxId());
    },
    onSuccess: (res) => {
      setIdleSuspended(false);
      if (res.outcome === 'paid') {
        patchDraft({ issuedTicketIds: res.issuedTicketIds ?? [] });
        navigate('/done', { replace: true });
      }
    },
    onError: () => setIdleSuspended(false),
  });

  useEffect(() => {
    setIdleSuspended(true);
    if (!startedRef.current && order !== null) {
      startedRef.current = true;
      payMutation.mutate();
    }
    return () => setIdleSuspended(false);
    // 刻意只在 mount 觸發一次（startedRef 守門避免重複付款）；deps 留空為設計意圖。
  }, []);

  if (order === null) return <Navigate to="/" replace />;

  const settled =
    payMutation.data !== undefined && payMutation.data.outcome !== 'paid' ? payMutation.data : null;
  const failed = settled !== null || payMutation.isError;

  if (!failed) {
    return (
      <KioskFrame showStatusBar={false} center>
        <div className="k-spinner" />
        <h1 className="k-h1" style={{ marginTop: 32 }}>
          {t('paying.authorizing')}
        </h1>
        <p className="k-text">{t('paying.title')}</p>
        <p className="k-text k-text--dim" style={{ marginTop: 16 }}>
          {t('paying.orderNo')}：{order.id.slice(0, 8).toUpperCase()}
        </p>
        <p className="k-text" style={{ marginTop: 24, fontWeight: 700 }}>
          {t('paying.doNotLeave')}
        </p>
        <p className="k-text k-text--dim" style={{ marginTop: 8 }}>
          ⓘ {t('paying.note')}
        </p>
      </KioskFrame>
    );
  }

  const isOversold = settled?.outcome === 'oversold';
  return (
    <KioskFrame center>
      <h1 className="k-h1" style={{ color: 'var(--c-danger)' }}>
        {isOversold ? t('paying.oversoldTitle') : t('paying.failedTitle')}
      </h1>
      <p className="k-text">{isOversold ? t('paying.oversoldDesc') : t('paying.failedDesc')}</p>
      <div className="k-footbar" style={{ marginTop: 32 }}>
        {!isOversold && (
          <KioskButton variant="primary" onClick={() => navigate('/payment', { replace: true })}>
            {t('paying.retry')}
          </KioskButton>
        )}
        <KioskButton
          variant="secondary"
          onClick={() => {
            resetSession();
            navigate('/', { replace: true });
          }}
        >
          {t('common.restart')}
        </KioskButton>
      </div>
    </KioskFrame>
  );
}
